import { useState } from "react";
import { services } from "@/data/mock";
import { motion, AnimatePresence } from "framer-motion";
import { Network, Camera, Sun, Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const iconMap = { Network, Camera, Sun };

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 }
};

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(services[0].id);
  const current = services.find((s) => s.id === activeService);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-onix-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.08),transparent_70%)]" />
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(rgba(6,182,212,0.05) 1px, transparent 1px)",
            backgroundSize: "50px 50px"
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Lo que hacemos</span>
            <h1 className="text-4xl md:text-5xl font-orbitron font-bold text-white mt-4 mb-6">
              Nuestros <span className="text-cyan-400">Servicios</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Soluciones especializadas en redes, seguridad y energía solar para hogares y empresas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Tab buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              const isActive = activeService === service.id;
              return (
                <button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`flex items-center gap-3 px-6 py-3.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 shadow-lg shadow-cyan-500/10"
                      : "bg-onix-card border border-white/5 text-slate-400 hover:text-white hover:border-white/10"
                  }`}
                >
                  {Icon && <Icon size={18} />}
                  {service.title}
                </button>
              );
            })}
          </div>

          {/* Active service content */}
          <AnimatePresence mode="wait">
            {current && (
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <div className="relative rounded-2xl overflow-hidden group">
                  <img
                    src={current.image}
                    alt={current.title}
                    className="w-full h-80 lg:h-[450px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-onix-dark/60 via-transparent to-transparent" />
                </div>

                <div>
                  <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                    {iconMap[current.icon] && (
                      <span className="text-cyan-400">
                        {(() => { const I = iconMap[current.icon]; return <I size={28} />; })()}
                      </span>
                    )}
                  </div>
                  <h2 className="text-3xl font-orbitron font-bold text-white mb-4">
                    {current.title}
                  </h2>
                  <p className="text-slate-400 leading-relaxed mb-8">
                    {current.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {current.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-cyan-500/15 flex items-center justify-center shrink-0 mt-0.5">
                          <Check size={12} className="text-cyan-400" />
                        </div>
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contacto"
                    className="group inline-flex items-center gap-2 px-8 py-3.5 bg-cyan-500 text-onix-dark font-semibold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                  >
                    Solicitar Cotización
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-20 bg-onix-darker">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl font-orbitron font-bold text-white">
              Todos nuestros <span className="text-cyan-400">servicios</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-all duration-300 group cursor-pointer"
                  onClick={() => {
                    setActiveService(service.id);
                    window.scrollTo({ top: 400, behavior: "smooth" });
                  }}
                >
                  <div className="w-14 h-14 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
                    {Icon && <Icon size={24} className="text-cyan-400" />}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">{service.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">{service.shortDesc}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-500 text-xs">
                        <Check size={12} className="text-cyan-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-orbitron font-bold text-white mb-6">
              ¿Necesitas una solución personalizada?
            </h2>
            <p className="text-slate-400 mb-10 max-w-xl mx-auto">
              Cada proyecto es único. Contáctanos para una evaluación gratuita y una propuesta diseñada a tu medida.
            </p>
            <Link
              to="/contacto"
              className="group inline-flex items-center gap-2 px-10 py-4 bg-cyan-500 text-onix-dark font-bold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
            >
              Solicitar Evaluación Gratuita
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
